import React from 'react';
import { Snackbar, IconButton, Icon, SnackbarContent } from '@material-ui/core';
import { green, amber, blue } from '@material-ui/core/colors';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import * as Actions from 'app/store/actions';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(theme => ({
	root: {},
	success: {
		backgroundColor: green[600],
		color: '#FFFFFF'
	},
	error: {
		backgroundColor: theme.palette.error.dark,
		color: theme.palette.getContrastText(theme.palette.error.dark)
	},
	info: {
		backgroundColor: blue[600],
		color: '#FFFFFF'
	},
	warning: {
		backgroundColor: amber[600],
		color: '#FFFFFF'
	},
	// Light yellow surface with dark yellow text — used for the deploy
	// failure alert. Matches the inline diagnosis banner family without
	// reusing the orange accent.
	yellow: {
		backgroundColor: '#FFF8E1',
		color: '#F57F17',
		'& .MuiIconButton-root': { color: '#F57F17' },
		'& .MuiIcon-root': { color: '#F57F17' }
	}
}));

const variantIcon = {
	success: 'check_circle',
	warning: 'warning',
	error: 'error_outline',
	info: 'info',
	yellow: 'warning'
};

const Message = () => {
	const dispatch = useDispatch();
	const state = useSelector(({ fuse }) => fuse.message.state);
	const options = useSelector(({ fuse }) => fuse.message.options);

	const classes = useStyles();

	return (
		<Snackbar
			{...options}
			open={state}
			onClose={(event, reason) => {
				// Persistent messages (autoHideDuration === null) only close
				// via the explicit close-button click (which bypasses this
				// handler) or a page refresh. Ignore clickaway / escape /
				// timeout so misclicks don't dismiss them.
				if (options.autoHideDuration === null) return;
				dispatch(Actions.hideMessage());
			}}
			classes={{
				root: classes.root
			}}
			ContentProps={{
				variant: 'body2',
				headlineMapping: {
					body1: 'div',
					body2: 'div'
				},
				role: (options.variant === 'error' || options.variant === 'yellow') ? 'alert' : 'status',
				'aria-live': (options.variant === 'error' || options.variant === 'yellow') ? 'assertive' : 'polite'
			}}
		>
			<SnackbarContent
				className={clsx(classes[options.variant])}
				message={
					<div className="flex items-center">
						{variantIcon[options.variant] && (
							<Icon className="mr-8" color="inherit">
								{variantIcon[options.variant]}
							</Icon>
						)}
						{options.message}
					</div>
				}
				action={[
					<IconButton
						key="close"
						aria-label="Close"
						color="inherit"
						onClick={() => {
							// If the message was tagged with a dedupKey, record
							// the dismissal so callers can suppress re-showing
							// it after a refresh. Key shape is producer-defined
							// (e.g. "deployFail:<clusterId>:<failedAt>").
							if (options.dedupKey) {
								try {
									localStorage.setItem('snackbar:dismissed:' + options.dedupKey, '1');
								} catch (_) { /* localStorage disabled — fall through */ }
							}
							dispatch(Actions.hideMessage());
						}}
					>
						<Icon>close</Icon>
					</IconButton>
				]}
			/>
		</Snackbar>
	);
};

export default React.memo(Message);
